import React, { useCallback } from "react";

import { LxTextInput } from "@labxchange/ui-components/lib/LxTextInput";

import { TableCellProps, CellChangeParams } from "../types";
import { useEditableCell } from "../hooks/useEditableCell";

export interface InputCellProps
  extends Pick<
    TableCellProps,
    | "cellId"
    | "rowIndex"
    | "colIndex"
    | "inputType"
    | "placeholder"
    | "value"
    | "disabled"
    | "showFeedback"
    | "isCorrect"
    | "feedback"
    | "unique"
    | "accuracy"
    | "colspan"
  > {
  hint?: string;
  isPrediction?: boolean;
  isSubmitted?: boolean;
  onChange?: (params: Omit<CellChangeParams, "componentID">) => void;
}

export const InputCell: React.FC<InputCellProps> = ({
  cellId,
  rowIndex,
  colIndex,
  inputType = "text",
  placeholder,
  value,
  disabled,
  showFeedback,
  isCorrect,
  feedback,
  unique,
  colspan,
  hint,
  isPrediction,
  isSubmitted,
  onChange,
}) => {
  const {
    cellClass,
    state,
    suffixIcon,
    translatedFeedback,
    translatedHint,
    shouldShowFeedback,
  } = useEditableCell({
    value,
    showFeedback,
    isCorrect,
    feedback,
    hint,
    isPrediction,
  });

  const isPredictionSubmitted = isPrediction && isSubmitted;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue =
        inputType === "number" || inputType === "num"
          ? e.target.value === ""
            ? null
            : Number(e.target.value)
          : e.target.value;

      onChange?.({
        rowId: rowIndex,
        colId: colIndex,
        value: newValue,
        unique,
      });
    },
    [onChange, rowIndex, colIndex, unique, inputType]
  );

  return (
    <td
      key={cellId}
      className={cellClass}
      colSpan={colspan ? Number(colspan) : undefined}
    >
      <LxTextInput
        name={cellId}
        type={inputType === "num" ? "number" : inputType}
        placeholder={
          placeholder
            ? { id: placeholder, defaultMessage: placeholder }
            : undefined
        }
        value={value !== null && value !== undefined ? String(value) : ""}
        onChange={handleChange}
        disabled={disabled || isPredictionSubmitted}
        state={state}
        suffix={suffixIcon}
        variant="borderless"
        feedback={translatedFeedback}
        showFeedback={shouldShowFeedback}
        hint={translatedHint}
        hintIconSize={24}
      />
    </td>
  );
};
